import moment from "moment";

import { isSpareStatus } from "./liftStatus";

/**
 * TE-2011: colour indicators for the Asset Tracking and Heartbeat Tracking
 * columns on the Lift Details grid.
 *
 * PBC found the old two-colour Asset Tracking dot confusing: it reported only
 * the antenna's connected flag, so a lift that had been silent for months and
 * one reporting normally both showed green. These helpers add an age dimension
 * so the page is actionable at a glance.
 *
 * Agreed rules
 * ------------
 * Heartbeat Tracking, and the map pin colour, share one rule so the two can
 * never disagree:
 *   grey    lift is Spare (always wins)
 *   n/a     has never reported a location
 *   green   valid location within the last 7 days
 *   red     more than 7 days since the last valid location
 *
 * Asset Tracking reports the antenna state only, with no age dimension:
 *   grey    lift is Spare (always wins)
 *   red     antenna reported disconnected at the last heartbeat
 *   green   antenna reported connected at the last heartbeat
 *   n/a     no antenna status reported
 *
 * There is deliberately no yellow in either indicator, and no RF recency
 * logic: an earlier iteration aged Asset Tracking on the last RFID read, which
 * was dropped in favour of the simpler antenna state. Nothing here reads
 * LAST_RF_REPORT_DATE any more.
 *
 * Both indicators revert from grey to the rules above when a lift is marked
 * back In Service.
 *
 * STALE_DAYS is the single threshold. PBC expect to revisit it (14 was
 * floated), so changing that constant is the whole edit.
 *
 * The age arrives from the API as `validLocationAgeHours`, computed
 * server-side from raw UTC. It is not derived here on purpose: the timestamps
 * in the payload have been through getLocalTime, which emits a naive DC-local
 * string with no offset, so ageing them against the browser clock would be
 * wrong by the DC's UTC offset.
 */

/** Days without a valid location before a lift is shown as inactive. */
export const STALE_DAYS = 7;

export const STALE_HOURS = STALE_DAYS * 24;

export const INDICATOR_COLOR = {
  GREEN: "#018015",
  RED: "#FF0000",
  GREY: "#9E9E9E",
};

/** Machine-readable indicator state, used for sorting, filtering and export. */
export const INDICATOR_STATE = {
  OK: "OK",
  CRITICAL: "CRITICAL",
  INACTIVE: "INACTIVE",
  UNKNOWN: "UNKNOWN",
};

const STATE_COLOR = {
  [INDICATOR_STATE.OK]: INDICATOR_COLOR.GREEN,
  [INDICATOR_STATE.CRITICAL]: INDICATOR_COLOR.RED,
  [INDICATOR_STATE.INACTIVE]: INDICATOR_COLOR.GREY,
  [INDICATOR_STATE.UNKNOWN]: null,
};

// Shown in the hover detail so the colour is self-explanatory, per PBC.
const COLOR_MEANING = {
  [INDICATOR_STATE.OK]: "Green = reporting normally",
  [INDICATOR_STATE.CRITICAL]: "Red = not reporting",
  [INDICATOR_STATE.INACTIVE]: "Grey = lift is Spare",
  [INDICATOR_STATE.UNKNOWN]: "n/a = nothing reported yet",
};

const ANTENNA_MEANING = {
  [INDICATOR_STATE.OK]: "Green = antenna connected at the last heartbeat",
  [INDICATOR_STATE.CRITICAL]: "Red = antenna disconnected at the last heartbeat",
  [INDICATOR_STATE.INACTIVE]: "Grey = lift is Spare",
  [INDICATOR_STATE.UNKNOWN]: "n/a = no antenna status reported",
};

/** Numeric age in hours, or null when absent or not a usable number. */
function toAgeHours(value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const n = Number(value);
  return Number.isFinite(n) ? Math.max(n, 0) : null;
}

/**
 * IRIS standard timestamp: 3-letter month, 2-digit day, 4-digit year,
 * 2-digit hour, 2-digit minute, AM/PM — e.g. "Oct 28, 2026 08:42 AM".
 *
 * Matches the format the grid's own date columns render through moment, so the
 * hover detail no longer shows the raw "2026-10-28 08:42:58" the API supplies.
 * Unparseable input falls back to the raw value rather than printing
 * "Invalid date".
 *
 * The incoming strings are naive DC-local (they have already been through
 * getLocalTime server-side and carry no offset), so they are parsed and
 * formatted as local and no timezone shift occurs. Do not add a timezone here.
 */
export function formatIndicatorTimestamp(value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("MMM DD, YYYY hh:mm A") : String(value);
}

/**
 * Hover detail for Asset Tracking.
 *
 * States the heartbeat the antenna status came from, because the status is
 * sticky: it is whatever the antenna last reported, however old that is.
 * Without the timestamp a user cannot tell a currently-connected antenna from
 * one that was connected six months ago.
 */
function buildAntennaTooltip({ lastReportedTime, state }) {
  const lines = ["Asset Tracking (RFID antenna)"];
  const reported = formatIndicatorTimestamp(lastReportedTime);
  lines.push(
    reported ? `Last Heartbeat: ${reported}` : "Last Heartbeat: never reported"
  );
  lines.push(ANTENNA_MEANING[state]);
  return lines.join("\n");
}

/**
 * Hover detail for Heartbeat Tracking.
 *
 * Shows BOTH timestamps, because the indicator is driven by the GPS one and a
 * user seeing red on a lift that is plainly communicating needs to be able to
 * tell the two apart without guessing.
 */
function buildHeartbeatTooltip({
  lastValidLocationDate,
  lastReportedTime,
  state,
}) {
  const lines = ["Heartbeat Tracking"];

  const validLocation = formatIndicatorTimestamp(lastValidLocationDate);
  lines.push(
    validLocation ? `Last valid location: ${validLocation}` : "Last valid location: never"
  );

  const reported = formatIndicatorTimestamp(lastReportedTime);
  lines.push(
    reported ? `Last communication: ${reported}` : "Last communication: never"
  );

  lines.push(COLOR_MEANING[state]);
  return lines.join("\n");
}

/**
 * Asset Tracking indicator for a lift row.
 *
 * Antenna state only — there is no age dimension here, unlike Heartbeat
 * Tracking. PBC asked for "the old one plus grey", so a disconnected antenna is
 * red regardless of how recently RF was seen, and Spare wins over both.
 *
 * @param {object} row Lift row from the lifts API.
 * @returns {{state: string, color: ?string, label: string, tooltip: string}}
 */
export function assetTrackingIndicator(row = {}) {
  const { status, antenna_connected: antennaConnected, lastReportedTime } = row;
  const antenna = String(antennaConnected ?? "").toLowerCase();

  const state = (() => {
    if (isSpareStatus(status)) {
      return INDICATOR_STATE.INACTIVE;
    }
    if (antenna === "false") {
      return INDICATOR_STATE.CRITICAL;
    }
    if (antenna === "true") {
      return INDICATOR_STATE.OK;
    }
    // No status reported at all. Note this is a sticky value: it reflects the
    // antenna at the lift's LAST heartbeat, however long ago that was, so a
    // long-silent lift can still show green here. Heartbeat Tracking is the
    // indicator that reveals that.
    return INDICATOR_STATE.UNKNOWN;
  })();

  return {
    state,
    color: STATE_COLOR[state],
    label: state === INDICATOR_STATE.UNKNOWN ? "n/a" : "",
    tooltip: buildAntennaTooltip({ lastReportedTime, state }),
  };
}

/**
 * Heartbeat Tracking indicator for a lift row.
 *
 * Ages on the last heartbeat that carried a REAL GPS FIX
 * (`validLocationAgeHours`), not on the last heartbeat of any kind.
 * DATE_UPDATED advances even when the device reports 0/0 coordinates, so a
 * lift with a dead GPS but working cellular would stay green indefinitely —
 * a false negative, and precisely the kind of silent fault this page exists to
 * surface. The trade is accepted: a lift parked inside a building or a trailer
 * for more than STALE_DAYS will show red despite communicating normally.
 *
 * A missing GPS date means n/a, per TE-2012's rule that n/a is for lifts yet
 * to report. This relies on LAST_VALID_LOCATION_DATE being backfilled: the
 * column shipped 2026-08-28 without one, leaving 1,437 of 2,918 prod rows
 * NULL, and until those are filled in those lifts read n/a rather than
 * green/red. The backfill (migration 002 on duo-iot-service-data
 * feature/TE-2008-location-backfill) is a prerequisite for this indicator
 * being trustworthy, not an optional tidy-up.
 *
 * The hover detail shows BOTH timestamps so the divergence between a working
 * cellular link and a dead GPS is visible rather than inferred.
 *
 * TE-2066: the relative "(3 days ago)" suffix was removed from the hover at
 * PBC's request, so `heartbeatAgeHours` is no longer read here. The absolute
 * timestamps carry the same information in the IRIS standard format, and the
 * age that drives the colour is still `validLocationAgeHours` above.
 *
 * @param {object} row Lift row from the lifts API.
 * @returns {{state: string, color: ?string, label: string, tooltip: string}}
 */
export function heartbeatIndicator(row = {}) {
  const { status, lastReportedTime, lastValidLocationDate } = row;
  const gpsAgeHours = toAgeHours(row.validLocationAgeHours);

  const state = (() => {
    if (isSpareStatus(status)) {
      return INDICATOR_STATE.INACTIVE;
    }
    if (gpsAgeHours === null) {
      return INDICATOR_STATE.UNKNOWN;
    }
    return gpsAgeHours <= STALE_HOURS ? INDICATOR_STATE.OK : INDICATOR_STATE.CRITICAL;
  })();

  return {
    state,
    color: STATE_COLOR[state],
    label: state === INDICATOR_STATE.UNKNOWN ? "n/a" : "",
    tooltip: buildHeartbeatTooltip({
      lastValidLocationDate,
      lastReportedTime,
      state,
    }),
  };
}

/**
 * Sort/filter/export key for an indicator cell.
 *
 * The grid needs a scalar per cell. Ordering runs worst-first so that sorting a
 * column ascending surfaces the lifts needing attention, which is the point of
 * the enhancement. Returned as the state name so CSV export stays readable.
 */
export function indicatorSortValue(indicator) {
  return indicator ? indicator.state : INDICATOR_STATE.UNKNOWN;
}

export const INDICATOR_SORT_ORDER = [
  INDICATOR_STATE.CRITICAL,
  INDICATOR_STATE.OK,
  INDICATOR_STATE.UNKNOWN,
  INDICATOR_STATE.INACTIVE,
];
